import { type Node, type NodeProps, Position } from '@xyflow/react';
import React from 'react';

import { createHandleId } from '../../utils/handle-ids';
import { BaseNode, type NodeData } from './BaseNode';
import { NodeHandle } from './NodeHandle';

export type JoinNodeType = Node<NodeData, 'custom_join'>;

export const JoinNode: React.FC<NodeProps<JoinNodeType>> = ({
  id,
  data,
  selected,
}) => {
  const baseDataset = data.config?.base_dataset as string;

  // Get connected target handles from enriched node data
  const connectedTargetHandles =
    (data.connectedTargetHandles as string[]) || [];

  // Calculate number of ports to show: N connected + 1 additional (minimum 1)
  const connectedCount = connectedTargetHandles.length;
  const totalPortsToShow = Math.max(1, connectedCount + 1);

  // Calculate dynamic dimensions based on port count
  const baseHeight = 60;
  const portSpacing = 20; // 20px spacing between ports
  const minHeight = baseHeight;
  const calculatedHeight = Math.max(
    minHeight,
    (totalPortsToShow - 1) * portSpacing + 40,
  ); // 40px for padding

  let subtitle: string | undefined;
  if (connectedCount > 0) {
    subtitle = `${connectedCount} dataset${connectedCount > 1 ? 's' : ''}`;
    if (baseDataset) {
      subtitle += ` from ${baseDataset}`;
    }
  } else {
    subtitle = 'Join datasets';
  }

  // Extract connection state from node data for visual indicators
  const activeConnection = data.activeConnection;
  const isValidTargetHandle = data.isValidTargetHandle as
    | ((targetHandle: string) => boolean)
    | undefined;
  const isActiveConnection = !!activeConnection;

  return (
    <BaseNode
      nodeId={id}
      data={data}
      category="flow"
      subtitle={subtitle}
      selected={selected}
      height={calculatedHeight}
    >
      {/* Dynamic input handles: show N connected + 1 additional */}
      {Array.from({ length: totalPortsToShow }, (_, i) => {
        // Calculate position for even distribution
        let topPercent: number;
        if (totalPortsToShow === 1) {
          topPercent = 50; // Center single handle
        } else {
          // Distribute evenly with 20% padding on top/bottom
          const padding = 20;
          const usableSpace = 100 - 2 * padding;
          topPercent = padding + (i * usableSpace) / (totalPortsToShow - 1);
        }

        return (
          <NodeHandle
            key={`dataset_${i}`}
            type="target"
            position={Position.Left}
            id={`dataset_${i}`}
            blockId={id}
            style={{ top: `${topPercent}%` }}
            isActiveConnection={isActiveConnection}
            isValidTarget={
              isValidTargetHandle
                ? isValidTargetHandle(createHandleId(id, `dataset_${i}`))
                : undefined
            }
          />
        );
      })}
      {/* Output handle for joined result */}
      <NodeHandle
        type="source"
        position={Position.Right}
        id="result"
        blockId={id}
        isActiveConnection={isActiveConnection}
      />
    </BaseNode>
  );
};
